import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { styled } from "@glitz/react";
import { spacing, fontSize } from "../theme";
import Base from "./basic/Base";
import Accordion from "./basic/Accordion";
import StripePaymentForm from "./StripePaymentForm";
import SwishPaymentPanel from "./SwishPaymentPanel";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const API_BASE = process.env.REACT_APP_API_URL || "";

export type PaymentMethodUsed = "swish" | "stripe";

interface Props {
  orderId: number | string | null;
  /** Visningsnummer (t.ex. TR-1003). Fallback: orderId. */
  orderNumber?: string | null;
  clientSecret?: string | null;
  returnUrl?: string;
  onCompleted: (paymentMethod?: PaymentMethodUsed) => void;
}

type StripeLoadState = { clientSecret: string | null; error: string | null };

const fetchPaymentIntent = async (
  orderId: number | string,
  paymentMethodTypes: string[]
): Promise<{ clientSecret: string } | { error: string }> => {
  const res = await fetch(`${API_BASE}/api/payments/stripe/create-payment-intent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ orderId, paymentMethodTypes }),
  });
  const text = await res.text();
  if (text.trim().startsWith("<")) {
    return { error: "Servern svarade med HTML. Kör både server och client (npm run dev från projektets rot), eller sätt REACT_APP_API_URL=http://localhost:5000 i client/.env och starta om." };
  }
  let data: { clientSecret?: string; error?: string };
  try {
    data = JSON.parse(text);
  } catch {
    return { error: "Ogiltigt svar från servern" };
  }
  if (!res.ok) return { error: data.error || "Kunde inte skapa betalning" };
  return { clientSecret: data.clientSecret! };
};

const updateOrderStatus = async (
  orderId: number | string,
  status: string,
  paymentMeta?: { payment_method?: string; payment_method_title?: string; transaction_id?: string }
) => {
  try {
    await fetch(`${API_BASE}/api/orders/${orderId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, ...paymentMeta }),
    });
  } catch (e) {
    console.error("Failed to update order status:", e);
  }
};

const CheckoutPaymentStep = ({
  orderId,
  orderNumber: orderNumberProp,
  clientSecret: _initialSecret,
  returnUrl,
  onCompleted,
}: Props) => {
  const [stripeState, setStripeState] = useState<StripeLoadState>({
    clientSecret: null,
    error: null,
  });
  const [completed, setCompleted] = useState(false);
  const [openPayment, setOpenPayment] = useState<"swish" | "stripe" | null>(
    "swish"
  );

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetchPaymentIntent(orderId, ["card", "klarna"]);
        if (cancelled) return;
        if ("clientSecret" in res) {
          setStripeState({ clientSecret: res.clientSecret, error: null });
        } else {
          setStripeState({ clientSecret: null, error: res.error });
        }
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Kunde inte ladda betalningsalternativ";
        setStripeState({ clientSecret: null, error: msg });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [orderId]);

  if (!orderId) {
    return (
      <Base>
        <TextWrapper>Ingen order vald…</TextWrapper>
      </Base>
    );
  }

  if (completed) {
    return (
      <Base>
        <TextWrapper>Betalning klar! Du kan nu se din orderbekräftelse.</TextWrapper>
      </Base>
    );
  }

  const renderStripeAccordion = () => {
    const { clientSecret: secret, error } = stripeState;
    if (!stripePromise) {
      return <AccordionMessage>Stripe är inte konfigurerat (saknar publishable key).</AccordionMessage>;
    }
    if (error) return <AccordionMessage>{error}</AccordionMessage>;
    if (!secret) return <AccordionMessage>Laddar betalning…</AccordionMessage>;
    return (
      <Elements
        stripe={stripePromise}
        options={{ clientSecret: secret, appearance: { theme: "stripe" } }}
      >
        <StripePaymentForm
          orderId={orderId}
          returnUrl={returnUrl}
          onSuccess={async (paymentIntentId) => {
            if (orderId)
              await updateOrderStatus(orderId, "processing", {
                payment_method: "stripe",
                payment_method_title: "Stripe",
                transaction_id: paymentIntentId,
              });
            setCompleted(true);
            onCompleted("stripe");
          }}
        />
      </Elements>
    );
  };

  return (
    <Base>
      <TextWrapper>Välj betalmetod</TextWrapper>
      <AccordionWrapper className="checkout-payment-accordion">
        <Accordion
          title="Betala med Swish"
          open={openPayment === "swish"}
          onToggle={(next) => setOpenPayment(next ? "swish" : null)}
        >
          <SwishPaymentPanel
            orderId={orderId}
            returnUrl={returnUrl}
            onPaid={() => {
              setCompleted(true);
              onCompleted("swish");
            }}
          />
        </Accordion>
        <Accordion
          title="Betala med kort / Klarna"
          open={openPayment === "stripe"}
          onToggle={(next) => setOpenPayment(next ? "stripe" : null)}
        >
          {renderStripeAccordion()}
        </Accordion>
      </AccordionWrapper>
    </Base>
  );
};

const TextWrapper = styled.div({
  textAlign: "center",
  marginTop: spacing.l,
  marginBottom: spacing.m,
  fontSize: fontSize.l,
});

const AccordionWrapper = styled.div({
  width: "100%",
  maxWidth: "480px",
  margin: { x: "auto", y: 0 },
  boxSizing: "border-box",
});

const AccordionMessage = styled.div({
  padding: { y: spacing.m },
  fontSize: fontSize.m,
});

export default CheckoutPaymentStep;
